/**
 * Verificación completa de una propuesta: une las reglas que no necesitan abrir el archivo con las
 * que sí, leyendo los PDF a través del backend de documentos.
 *
 * Es la única pieza de la verificación que habla con la red. Las reglas viven en
 * `verificacionAdministrativa` y `verificacionLectura`, y son funciones puras.
 *
 * Tolera que el análisis de PDF no esté disponible: si el backend no responde, la verificación
 * entrega igual los hallazgos que no dependen del archivo. Así el botón sigue sirviendo aunque el
 * endpoint todavía no esté desplegado.
 */

import apiDocumentos from '../api/Documentos/api-documentos';
import { runWithConcurrency } from './concurrencia';
import {
  aplicarVerificacion,
  EtapaVerificacion,
  ResultadoAplicacion,
  TipoDocumento,
  verificarDocumentosAdministrativos,
} from './verificacionAdministrativa';
import { ContextoLectura, LecturaDocumento, verificarConLectura } from './verificacionLectura';
import { aplicarVerificacionTecnicos } from './verificacionTecnicos';

/** PDF que se analizan a la vez dentro de una misma propuesta. */
const LECTURAS_EN_PARALELO = 3;

export type ResultadoPropuesta = ResultadoAplicacion & {
  /** Cuántos documentos se alcanzaron a leer */
  leidos: number;
  /** Documentos con archivo que no se pudieron leer: van a la fase con OCR */
  sinLeer: number;
  /** Copia de los documentos técnicos con sus marcas, cuando se revisaron */
  documentosTecnicos: any[];
};

/**
 * Descarga y analiza los PDF de los documentos que la fase determinista dejó pendientes.
 * Devuelve las lecturas indexadas por la posición del documento en la postulación.
 */
const leerDocumentos = async (
  pendientes: { indice: number; nombre: string; tipo: TipoDocumento }[],
  documentos: any[]
): Promise<{ [indice: number]: LecturaDocumento }> => {
  const lecturas: { [indice: number]: LecturaDocumento } = {};

  await runWithConcurrency(
    pendientes,
    async ({ indice }) => {
      const archivo = documentos[indice]?.url_participante;
      if (!archivo) return;

      const analisis = await apiDocumentos.analizarDocumento(archivo);
      if (analisis?.ok) {
        lecturas[indice] = {
          cifrado: analisis.cifrado,
          texto: analisis.texto,
          utilizable: analisis.utilizable,
        };
      }
    },
    LECTURAS_EN_PARALELO
  );

  return lecturas;
};

/**
 * Verifica una propuesta completa y devuelve los documentos ya marcados, listos para guardar.
 *
 * El contexto aporta los datos de la postulación contra los que se contrasta el contenido: el
 * documento del participante y la fecha de apertura de la convocatoria.
 */
export const verificarPropuesta = async (
  documentosAdministrativos: any,
  fecha: string,
  contexto: ContextoLectura = {},
  opciones: {
    respetarDecisionHumana?: boolean;
    etapa?: EtapaVerificacion;
    /** Documentos técnicos de la misma propuesta, si se quieren revisar en la misma pasada */
    documentosTecnicos?: any;
  } = {}
): Promise<ResultadoPropuesta> => {
  const documentos = Array.isArray(documentosAdministrativos) ? documentosAdministrativos : [];
  const previo = verificarDocumentosAdministrativos(documentos);

  const lecturas = await leerDocumentos(previo.requierenLectura, documentos);
  const hallazgosLectura = verificarConLectura(previo.requierenLectura, lecturas, contexto);

  const aplicado = aplicarVerificacion(documentos, fecha, {
    ...opciones,
    hallazgosAdicionales: hallazgosLectura,
  });

  // Los técnicos no son subsanables: su ausencia es rechazo en cualquier etapa, así que no
  // dependen de la etapa como sí lo hacen los administrativos.
  const tecnicos = aplicarVerificacionTecnicos(opciones.documentosTecnicos, fecha, {
    respetarDecisionHumana: opciones.respetarDecisionHumana,
  });

  const leidos = Object.keys(lecturas).length;
  const observacion = [aplicado.observacion, tecnicos.observacion].filter(Boolean).join('\n');

  return {
    ...aplicado,
    observacion,
    rechazados: aplicado.rechazados + tecnicos.rechazados,
    respetados: aplicado.respetados + tecnicos.respetados,
    documentosTecnicos: tecnicos.documentos,
    leidos,
    sinLeer: previo.requierenLectura.length - leidos,
  };
};

/** Arma el contexto de lectura a partir de la postulación. */
export const contextoDeLaPostulacion = (postulacion: any): ContextoLectura => ({
  numeroDocumentoParticipante: postulacion?.numero_documento_participante,
  fechaApertura: postulacion?.fecha_apertura ? new Date(postulacion.fecha_apertura) : null,
});
